import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, Edit, Trash2, Search, Building2, Phone, Mail, 
  MapPin, Globe, CheckCircle, XCircle, Star, Upload, Image,
  Link2, Link2Off, RefreshCw, ExternalLink, User, Landmark,
  FileText, Users, X, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '../components/ImageUpload';
import AnexosManager from '../components/AnexosManager';
import AnexosErrorBoundary from '../components/AnexosErrorBoundary';
import { TitleWithHelp } from '../components/ui/help-tooltip';
import { ViewToggle } from '../components/ui/view-toggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

// Configuração de ajuda do módulo
const HELP_CONFIG = {
  title: "Cadastro de Empresas",
  description: "Gerencie as empresas do seu grupo. Cada empresa pode ter sua própria configuração fiscal, dados bancários e integração com o Conta Azul para sincronização automática de contas a pagar.",
  connections: [
    { module: "Cotações", description: "A empresa selecionada define o cabeçalho e dados fiscais nas cotações de compra." },
    { module: "Pedidos de Compra", description: "Pedidos são vinculados à empresa emissora para controle financeiro." },
    { module: "Conta Azul", description: "Integração para criar automaticamente contas a pagar quando pedidos são aprovados." },
    { module: "PDF de Cotação", description: "A logo e dados da empresa aparecem no cabeçalho dos PDFs gerados." }
  ],
  tips: [
    "Defina uma empresa como 'padrão' para agilizar a criação de cotações.",
    "Conecte ao Conta Azul para automatizar o financeiro.",
    "Cadastre contatos adicionais para facilitar a comunicação.",
    "A logo da empresa aparecerá nos PDFs de cotação."
  ]
};

const Empresas = () => {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInativas, setShowInativas] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [deleteEmpresaId, setDeleteEmpresaId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  const emptyForm = {
    // Dados básicos
    tipo_pessoa: 'juridica',
    razao_social: '',
    nome_fantasia: '',
    cnpj: '',
    cpf: '',
    
    // Dados fiscais
    inscricao_estadual: '',
    inscricao_municipal: '',
    isento_inscricao_estadual: false,
    cnae_primario: '',
    regime_tributacao: '',
    optante_mei: false,
    data_fundacao: '',
    
    // O que a empresa vende
    vende_produto: true,
    vende_servico: false,
    
    // Endereço principal
    endereco: {
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: '',
    },
    
    // Endereços adicionais
    enderecos_adicionais: [],
    
    // Contato principal
    telefone: '',
    celular: '',
    email: '',
    site: '',
    
    // Contatos adicionais
    contatos: [],
    
    // Logo e dados bancários
    logo_url: '',
    dados_bancarios: {
      banco: '',
      agencia: '',
      conta: '',
      tipo: 'corrente',
      pix: '',
    },
    
    // Configurações
    empresa_padrao: false,
    observacoes: '',
    
    // Anexos / Documentação
    anexos: [],
  };

  const [form, setForm] = useState(emptyForm);

  // Estado para status do Conta Azul por empresa
  const [contaAzulStatus, setContaAzulStatus] = useState({});

  useEffect(() => {
    document.title = "Empresas — NautilusOS";
    fetchEmpresas();
  }, [showInativas]);

  // Busca status do Conta Azul para todas empresas integradas
  const fetchContaAzulStatus = async (empresasList) => {
    const statusPromises = empresasList
      .filter(e => e.conta_azul?.integrado)
      .map(async (empresa) => {
        try {
          const response = await api.get(`/conta-azul/status/${empresa.id}`);
          return { empresaId: empresa.id, ...response.data };
        } catch (error) {
          return { empresaId: empresa.id, error: true };
        }
      });
    
    const results = await Promise.all(statusPromises);
    const statusMap = {};
    results.forEach(result => {
      if (result.empresaId) {
        statusMap[result.empresaId] = result;
      }
    });
    setContaAzulStatus(statusMap);
  };

  const fetchEmpresas = async () => {
    setLoading(true);
    try {
      const params = showInativas ? { ativa: false } : {};
      const response = await api.get(`/empresas`, { params });
      const empresasList = response.data.items || [];
      setEmpresas(empresasList);
      
      // Buscar status do Conta Azul após carregar empresas
      fetchContaAzulStatus(empresasList);
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleNovaEmpresa = () => {
    setEditingEmpresa(null);
    setForm(emptyForm);
    setShowDialog(true);
  };

  const handleEditEmpresa = (empresa) => {
    setEditingEmpresa(empresa);
    setForm({
      tipo_pessoa: empresa.tipo_pessoa || 'juridica',
      razao_social: empresa.razao_social || '',
      nome_fantasia: empresa.nome_fantasia || '',
      cnpj: empresa.cnpj || '',
      cpf: empresa.cpf || '',
      inscricao_estadual: empresa.inscricao_estadual || '',
      inscricao_municipal: empresa.inscricao_municipal || '',
      isento_inscricao_estadual: empresa.isento_inscricao_estadual || false,
      cnae_primario: empresa.cnae_primario || '',
      regime_tributacao: empresa.regime_tributacao || '',
      optante_mei: empresa.optante_mei || false,
      data_fundacao: empresa.data_fundacao || '',
      vende_produto: empresa.vende_produto ?? true,
      vende_servico: empresa.vende_servico || false,
      endereco: empresa.endereco || emptyForm.endereco,
      enderecos_adicionais: empresa.enderecos_adicionais || [],
      telefone: empresa.telefone || '',
      celular: empresa.celular || '',
      email: empresa.email || '',
      site: empresa.site || '',
      contatos: empresa.contatos || [],
      logo_url: empresa.logo_url || '',
      dados_bancarios: empresa.dados_bancarios || emptyForm.dados_bancarios,
      empresa_padrao: empresa.empresa_padrao || false,
      observacoes: empresa.observacoes || '',
      anexos: empresa.anexos || [],
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    // Validação baseada no tipo de pessoa
    if (form.tipo_pessoa === 'juridica') {
      if (!form.razao_social.trim()) {
        toast.error('Razão Social é obrigatória');
        return;
      }
      if (!form.cnpj.trim()) {
        toast.error('CNPJ é obrigatório');
        return;
      }
    } else {
      if (!form.razao_social.trim()) {
        toast.error('Nome completo é obrigatório');
        return;
      }
      if (!form.cpf.trim()) {
        toast.error('CPF é obrigatório');
        return;
      }
    }

    setSubmitLoading(true);
    try {
      if (editingEmpresa) {
        await api.put(`/empresas/${editingEmpresa.id}`, form);
        toast.success('Empresa atualizada!');
      } else {
        await api.post('/empresas', form);
        toast.success('Empresa criada!');
      }
      setShowDialog(false);
      fetchEmpresas();
    } catch (error) {
      console.error('Erro ao salvar empresa:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar empresa');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Funções para gerenciar contatos adicionais
  const addContato = () => {
    const newContato = {
      id: crypto.randomUUID(),
      nome: '',
      cargo: '',
      departamento: '',
      telefone: '',
      celular: '',
      email: '',
      principal: false,
    };
    setForm(prev => ({
      ...prev,
      contatos: [...prev.contatos, newContato]
    }));
  };

  const updateContato = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      contatos: prev.contatos.map((c, i) => 
        i === index ? { ...c, [field]: value } : c
      )
    }));
  };

  const removeContato = (index) => {
    setForm(prev => ({
      ...prev,
      contatos: prev.contatos.filter((_, i) => i !== index)
    }));
  };

  // Funções para gerenciar endereços adicionais
  const addEndereco = () => {
    const newEndereco = {
      id: crypto.randomUUID(),
      tipo: 'comercial',
      descricao: '',
      logradouro: '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      estado: '',
      cep: '',
    };
    setForm(prev => ({
      ...prev,
      enderecos_adicionais: [...prev.enderecos_adicionais, newEndereco]
    }));
  };

  const updateEndereco = (index, field, value) => {
    setForm(prev => ({
      ...prev,
      enderecos_adicionais: prev.enderecos_adicionais.map((e, i) => 
        i === index ? { ...e, [field]: value } : e
      )
    }));
  };

  const removeEndereco = (index) => {
    setForm(prev => ({
      ...prev,
      enderecos_adicionais: prev.enderecos_adicionais.filter((_, i) => i !== index)
    }));
  };

  const handleDelete = async () => {
    if (!deleteEmpresaId) return;

    try {
      await api.delete(`/empresas/${deleteEmpresaId}`);
      toast.success('Empresa desativada!');
      setDeleteEmpresaId(null);
      fetchEmpresas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desativar empresa');
    }
  };

  const handleConectarContaAzul = async (empresaId) => {
    try {
      // Buscar URL de autorização do backend
      const response = await api.get(`/conta-azul/auth/url/${empresaId}`);
      const { auth_url } = response.data;
      
      if (auth_url) {
        // Abrir em nova janela para não perder o contexto
        window.open(auth_url, '_blank', 'width=600,height=700');
        toast.info('Faça login no Conta Azul na janela que abriu. Após autorizar, volte aqui e atualize a página.');
      } else {
        toast.error('Erro ao gerar link de autorização');
      }
    } catch (error) {
      console.error('Erro ao conectar Conta Azul:', error);
      toast.error(error.response?.data?.detail || 'Erro ao conectar com Conta Azul');
    }
  };

  const handleDesconectarContaAzul = async (empresaId) => {
    try {
      await api.post(`/conta-azul/desconectar/${empresaId}`, {});
      toast.success('Integração com Conta Azul desconectada');
      fetchEmpresas();
    } catch (error) {
      console.error('Erro ao desconectar Conta Azul:', error);
      toast.error(error.response?.data?.detail || 'Erro ao desconectar');
    }
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .substring(0, 18);
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers
        .replace(/^(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .substring(0, 14);
    }
    return numbers
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .substring(0, 15);
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/^(\d{5})(\d)/, '$1-$2').substring(0, 9);
  };

  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      .substring(0, 14);
  };

  const filteredEmpresas = empresas.filter(empresa => {
    const search = searchTerm.toLowerCase();
    return (
      empresa.razao_social?.toLowerCase().includes(search) ||
      empresa.nome_fantasia?.toLowerCase().includes(search) ||
      empresa.cnpj?.includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="empresas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <TitleWithHelp
            title="Cadastro de Empresas"
            description="Gerencie as empresas do grupo. Cada empresa pode ter configuração fiscal, dados bancários e integração com Conta Azul."
            connections={['Cotações', 'Pedidos de Compra', 'Conta Azul']}
          >
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-blue-600" />
              Empresas
            </h1>
          </TitleWithHelp>
          <p className="text-gray-500 mt-1">Gerencie as empresas do grupo (multi-CNPJ)</p>
        </div>
        <div className="flex items-center gap-3">
          <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
          <Button onClick={handleNovaEmpresa} data-testid="btn-nova-empresa">
            <Plus className="w-4 h-4 mr-2" />
            Nova Empresa
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar por razão social, nome fantasia ou CNPJ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-empresas"
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch
            checked={showInativas}
            onCheckedChange={setShowInativas}
            id="show-inativas"
          />
          <Label htmlFor="show-inativas" className="text-sm text-gray-600">
            Mostrar inativas
          </Label>
        </div>
      </div>

      {/* Lista de Empresas */}
      {filteredEmpresas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {searchTerm ? 'Nenhuma empresa encontrada' : 'Nenhuma empresa cadastrada'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm ? 'Tente ajustar os filtros de busca' : 'Cadastre a primeira empresa do grupo'}
            </p>
            {!searchTerm && (
              <Button onClick={handleNovaEmpresa}>
                <Plus className="w-4 h-4 mr-2" />
                Nova Empresa
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === 'list' ? (
        <Card data-testid="empresas-list-view">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50/80">
                  <th className="text-left p-3 font-medium text-gray-600">Empresa</th>
                  <th className="text-left p-3 font-medium text-gray-600">CNPJ</th>
                  <th className="text-left p-3 font-medium text-gray-600">Telefone</th>
                  <th className="text-left p-3 font-medium text-gray-600">Email</th>
                  <th className="text-left p-3 font-medium text-gray-600">Cidade/UF</th>
                  <th className="text-left p-3 font-medium text-gray-600">Conta Azul</th>
                  <th className="text-left p-3 font-medium text-gray-600">Status</th>
                  <th className="text-right p-3 font-medium text-gray-600">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmpresas.map(empresa => (
                  <tr key={empresa.id} className={`border-b hover:bg-blue-50/40 transition-colors group ${!empresa.ativa ? 'opacity-60' : ''}`} data-testid={`empresa-row-${empresa.id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        {empresa.logo_url && <img src={empresa.logo_url} alt="" className="w-6 h-6 object-contain rounded" />}
                        <div>
                          <span className="font-medium text-gray-900">{empresa.nome_fantasia || empresa.razao_social}</span>
                          {empresa.empresa_padrao && <Badge className="ml-2 bg-yellow-100 text-yellow-700 text-[10px]">Padrão</Badge>}
                          {empresa.nome_fantasia && <p className="text-xs text-gray-400">{empresa.razao_social}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-gray-600 font-mono text-xs">{empresa.cnpj}</td>
                    <td className="p-3 text-gray-600">{empresa.telefone || '—'}</td>
                    <td className="p-3 text-gray-600 max-w-[200px] truncate">{empresa.email || '—'}</td>
                    <td className="p-3 text-gray-600">{empresa.endereco?.cidade ? `${empresa.endereco.cidade}/${empresa.endereco.estado}` : '—'}</td>
                    <td className="p-3">
                      {empresa.conta_azul?.integrado ? (
                        <Badge className="bg-blue-100 text-blue-700 text-[10px]">Conectado</Badge>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="p-3">
                      <Badge className={empresa.ativa ? 'bg-green-100 text-green-700 text-[10px]' : 'bg-gray-100 text-gray-500 text-[10px]'}>
                        {empresa.ativa ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEditEmpresa(empresa)} title="Editar">
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        {!empresa.empresa_padrao && (
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => setDeleteEmpresaId(empresa.id)} title="Excluir">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredEmpresas.map(empresa => (
            <Card 
              key={empresa.id} 
              className={`hover:shadow-md transition-shadow ${!empresa.ativa ? 'opacity-60' : ''}`}
              data-testid={`empresa-card-${empresa.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {empresa.empresa_padrao && (
                        <Badge className="bg-yellow-100 text-yellow-700">
                          <Star className="w-3 h-3 mr-1" />
                          Padrão
                        </Badge>
                      )}
                      {empresa.conta_azul?.integrado ? (
                        <Badge className="bg-blue-100 text-blue-700">
                          <Link2 className="w-3 h-3 mr-1" />
                          Conta Azul
                        </Badge>
                      ) : null}
                      {!empresa.ativa && (
                        <Badge variant="secondary">Inativa</Badge>
                      )}
                    </div>
                    <CardTitle className="text-base truncate">
                      {empresa.nome_fantasia || empresa.razao_social}
                    </CardTitle>
                    {empresa.nome_fantasia && (
                      <p className="text-xs text-gray-500 truncate">{empresa.razao_social}</p>
                    )}
                  </div>
                  {empresa.logo_url && (
                    <img 
                      src={empresa.logo_url} 
                      alt="Logo" 
                      className="w-12 h-12 object-contain rounded"
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Building2 className="w-4 h-4 flex-shrink-0" />
                    <span className="font-mono">{empresa.cnpj}</span>
                  </div>
                  {empresa.telefone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      <span>{empresa.telefone}</span>
                    </div>
                  )}
                  {empresa.email && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{empresa.email}</span>
                    </div>
                  )}
                  {empresa.endereco?.cidade && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4 flex-shrink-0" />
                      <span>{empresa.endereco.cidade}/{empresa.endereco.estado}</span>
                    </div>
                  )}
                </div>

                {/* Seção Conta Azul */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Integração Conta Azul</span>
                    {empresa.conta_azul?.integrado ? (
                      <Badge className={`text-xs ${contaAzulStatus[empresa.id]?.token_valid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {contaAzulStatus[empresa.id]?.token_valid ? '✓ Conectado' : '⚠ Token expirado'}
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-600 text-xs">Não conectado</Badge>
                    )}
                  </div>
                  
                  {empresa.conta_azul?.integrado ? (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConectarContaAzul(empresa.id)}
                        className="flex-1 text-xs"
                        data-testid={`btn-reconectar-ca-${empresa.id}`}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Reconectar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDesconectarContaAzul(empresa.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs"
                        data-testid={`btn-desconectar-ca-${empresa.id}`}
                      >
                        <Link2Off className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleConectarContaAzul(empresa.id)}
                      className="w-full text-blue-600 border-blue-200 hover:bg-blue-50 text-xs"
                      data-testid={`btn-conectar-ca-${empresa.id}`}
                    >
                      <Link2 className="w-3 h-3 mr-1" />
                      Conectar ao Conta Azul
                    </Button>
                  )}
                </div>

                {/* Ações da Empresa */}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditEmpresa(empresa)}
                    className="flex-1"
                    data-testid={`btn-edit-${empresa.id}`}
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  {empresa.ativa && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteEmpresaId(empresa.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`btn-delete-${empresa.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Nova/Editar Empresa */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEmpresa ? 'Editar Empresa' : 'Nova Empresa'}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="dados" className="w-full mt-4">
            <TabsList className="grid w-full grid-cols-6">
              <TabsTrigger value="dados" className="text-xs">
                <Building2 className="w-3 h-3 mr-1" />
                Dados
              </TabsTrigger>
              <TabsTrigger value="endereco" className="text-xs">
                <MapPin className="w-3 h-3 mr-1" />
                Endereços
              </TabsTrigger>
              <TabsTrigger value="contatos" className="text-xs">
                <Users className="w-3 h-3 mr-1" />
                Contatos
              </TabsTrigger>
              <TabsTrigger value="bancario" className="text-xs">
                <Landmark className="w-3 h-3 mr-1" />
                Bancário
              </TabsTrigger>
              <TabsTrigger value="anexos" className="text-xs" data-testid="empresa-tab-anexos">
                <FileText className="w-3 h-3 mr-1" />
                Anexos
              </TabsTrigger>
              <TabsTrigger value="adicional" className="text-xs">
                <FileText className="w-3 h-3 mr-1" />
                Adicional
              </TabsTrigger>
            </TabsList>

            {/* ABA DADOS PRINCIPAIS */}
            <TabsContent value="dados" className="space-y-4 mt-4">
              {/* Logo da Empresa */}
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0 w-32">
                  <ImageUpload
                    value={form.logo_url}
                    onChange={(url) => setForm(prev => ({ ...prev, logo_url: url }))}
                    label="Logo da Empresa"
                    placeholder="Upload da logo..."
                    modulo="empresas"
                    entidadeId={editingEmpresa?.id}
                  />
                </div>
                <div className="flex-1 space-y-4">
                  {/* Tipo de Pessoa */}
                  <div>
                    <Label className="text-sm font-medium">Tipo de Pessoa</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="tipo_pessoa"
                          value="juridica"
                          checked={form.tipo_pessoa === 'juridica'}
                          onChange={(e) => setForm(prev => ({ ...prev, tipo_pessoa: e.target.value }))}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">Pessoa Jurídica (CNPJ)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="tipo_pessoa"
                          value="fisica"
                          checked={form.tipo_pessoa === 'fisica'}
                          onChange={(e) => setForm(prev => ({ ...prev, tipo_pessoa: e.target.value }))}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span className="text-sm">Pessoa Física (CPF)</span>
                      </label>
                    </div>
                  </div>

                  {/* Nome / Razão Social */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{form.tipo_pessoa === 'juridica' ? 'Razão Social *' : 'Nome Completo *'}</Label>
                      <Input
                        value={form.razao_social}
                        onChange={(e) => setForm(prev => ({ ...prev, razao_social: e.target.value }))}
                        placeholder={form.tipo_pessoa === 'juridica' ? 'Nome completo da empresa' : 'Nome completo'}
                        data-testid="input-razao-social"
                      />
                    </div>
                    <div>
                      <Label>{form.tipo_pessoa === 'juridica' ? 'Nome Fantasia' : 'Apelido'}</Label>
                      <Input
                        value={form.nome_fantasia}
                        onChange={(e) => setForm(prev => ({ ...prev, nome_fantasia: e.target.value }))}
                        placeholder={form.tipo_pessoa === 'juridica' ? 'Nome comercial' : 'Apelido (opcional)'}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Documentos */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {form.tipo_pessoa === 'juridica' ? (
                  <>
                    <div>
                      <Label>CNPJ *</Label>
                      <Input
                        value={form.cnpj}
                        onChange={(e) => setForm(prev => ({ ...prev, cnpj: formatCNPJ(e.target.value) }))}
                        placeholder="00.000.000/0000-00"
                        data-testid="input-cnpj"
                      />
                    </div>
                    <div>
                      <Label>Inscrição Estadual</Label>
                      <div className="space-y-1">
                        <Input
                          value={form.inscricao_estadual}
                          onChange={(e) => setForm(prev => ({ ...prev, inscricao_estadual: e.target.value }))}
                          placeholder="Número IE"
                          disabled={form.isento_inscricao_estadual}
                        />
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.isento_inscricao_estadual}
                            onChange={(e) => setForm(prev => ({ 
                              ...prev, 
                              isento_inscricao_estadual: e.target.checked,
                              inscricao_estadual: e.target.checked ? 'ISENTO' : ''
                            }))}
                            className="w-3 h-3"
                          />
                          <span className="text-xs text-gray-500">Isento</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <Label>Inscrição Municipal</Label>
                      <Input
                        value={form.inscricao_municipal}
                        onChange={(e) => setForm(prev => ({ ...prev, inscricao_municipal: e.target.value }))}
                        placeholder="Número IM"
                      />
                    </div>
                    <div>
                      <Label>CNAE Principal</Label>
                      <Input
                        value={form.cnae_primario}
                        onChange={(e) => setForm(prev => ({ ...prev, cnae_primario: e.target.value }))}
                        placeholder="0000-0/00"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label>CPF *</Label>
                      <Input
                        value={form.cpf}
                        onChange={(e) => setForm(prev => ({ ...prev, cpf: formatCPF(e.target.value) }))}
                        placeholder="000.000.000-00"
                        data-testid="input-cpf"
                      />
                    </div>
                    <div>
                      <Label>RG</Label>
                      <Input
                        value={form.inscricao_estadual}
                        onChange={(e) => setForm(prev => ({ ...prev, inscricao_estadual: e.target.value }))}
                        placeholder="Número RG"
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Dados Fiscais (apenas PJ) */}
              {form.tipo_pessoa === 'juridica' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label>Regime de Tributação</Label>
                    <Select
                      value={form.regime_tributacao}
                      onValueChange={(value) => setForm(prev => ({ ...prev, regime_tributacao: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="simples_nacional">Simples Nacional</SelectItem>
                        <SelectItem value="lucro_presumido">Lucro Presumido</SelectItem>
                        <SelectItem value="lucro_real">Lucro Real</SelectItem>
                        <SelectItem value="mei">MEI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data de Fundação</Label>
                    <Input
                      type="date"
                      value={form.data_fundacao}
                      onChange={(e) => setForm(prev => ({ ...prev, data_fundacao: e.target.value }))}
                    />
                  </div>
                  <div className="flex items-end gap-4">
                    <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-md hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={form.vende_produto}
                        onChange={(e) => setForm(prev => ({ ...prev, vende_produto: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Vende Produtos</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer p-2 border rounded-md hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={form.vende_servico}
                        onChange={(e) => setForm(prev => ({ ...prev, vende_servico: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Presta Serviços</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Empresa Padrão */}
              <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <Switch
                  checked={form.empresa_padrao}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, empresa_padrao: checked }))}
                  id="empresa-padrao"
                />
                <Label htmlFor="empresa-padrao" className="flex items-center gap-2 cursor-pointer">
                  <Star className="w-4 h-4 text-yellow-600" />
                  Definir como empresa padrão para novas cotações/pedidos
                </Label>
              </div>
            </TabsContent>

            {/* ABA ENDEREÇOS */}
            <TabsContent value="endereco" className="space-y-6 mt-4">
              {/* Endereço Principal */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Endereço Principal
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-2">
                    <Label>Logradouro</Label>
                    <Input
                      value={form.endereco.logradouro}
                      onChange={(e) => setForm(prev => ({ 
                        ...prev, 
                        endereco: { ...prev.endereco, logradouro: e.target.value }
                      }))}
                      placeholder="Rua, Avenida, etc"
                    />
                  </div>
                  <div>
                    <Label>Número</Label>
                    <Input
                      value={form.endereco.numero}
                      onChange={(e) => setForm(prev => ({ 
                        ...prev, 
                        endereco: { ...prev.endereco, numero: e.target.value }
                      }))}
                      placeholder="Nº"
                    />
                  </div>
                  <div>
                    <Label>Complemento</Label>
                    <Input
                      value={form.endereco.complemento}
                      onChange={(e) => setForm(prev => ({ 
                        ...prev, 
                        endereco: { ...prev.endereco, complemento: e.target.value }
                      }))}
                      placeholder="Sala, Andar..."
                    />
                  </div>
                  <div>
                    <Label>Bairro</Label>
                    <Input
                      value={form.endereco.bairro}
                      onChange={(e) => setForm(prev => ({ 
                        ...prev, 
                        endereco: { ...prev.endereco, bairro: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label>Cidade</Label>
                    <Input
                      value={form.endereco.cidade}
                      onChange={(e) => setForm(prev => ({ 
                        ...prev, 
                        endereco: { ...prev.endereco, cidade: e.target.value }
                      }))}
                    />
                  </div>
                  <div>
                    <Label>Estado</Label>
                    <Input
                      value={form.endereco.estado}
                      onChange={(e) => setForm(prev => ({ 
                        ...prev, 
                        endereco: { ...prev.endereco, estado: e.target.value.toUpperCase().substring(0, 2) }
                      }))}
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label>CEP</Label>
                    <Input
                      value={form.endereco.cep}
                      onChange={(e) => setForm(prev => ({ 
                        ...prev, 
                        endereco: { ...prev.endereco, cep: formatCEP(e.target.value) }
                      }))}
                      placeholder="00000-000"
                    />
                  </div>
                </div>
              </div>

              {/* Endereços Adicionais */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Endereços Adicionais
                  </h3>
                  <Button type="button" variant="outline" size="sm" onClick={addEndereco}>
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Endereço
                  </Button>
                </div>

                {form.enderecos_adicionais.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4 border-2 border-dashed rounded-lg">
                    Nenhum endereço adicional cadastrado
                  </p>
                ) : (
                  <div className="space-y-4">
                    {form.enderecos_adicionais.map((endereco, index) => (
                      <div key={endereco.id} className="p-4 border rounded-lg bg-gray-50 relative">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 h-6 w-6 text-red-500 hover:text-red-700"
                          onClick={() => removeEndereco(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs">Tipo</Label>
                            <Select
                              value={endereco.tipo}
                              onValueChange={(value) => updateEndereco(index, 'tipo', value)}
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="comercial">Comercial</SelectItem>
                                <SelectItem value="filial">Filial</SelectItem>
                                <SelectItem value="deposito">Depósito</SelectItem>
                                <SelectItem value="entrega">Entrega</SelectItem>
                                <SelectItem value="cobranca">Cobrança</SelectItem>
                                <SelectItem value="outro">Outro</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="md:col-span-3">
                            <Label className="text-xs">Descrição</Label>
                            <Input
                              value={endereco.descricao}
                              onChange={(e) => updateEndereco(index, 'descricao', e.target.value)}
                              placeholder="Ex: Matriz, Filial Centro..."
                              className="h-8"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <Label className="text-xs">Logradouro</Label>
                            <Input
                              value={endereco.logradouro}
                              onChange={(e) => updateEndereco(index, 'logradouro', e.target.value)}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Número</Label>
                            <Input
                              value={endereco.numero}
                              onChange={(e) => updateEndereco(index, 'numero', e.target.value)}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Complemento</Label>
                            <Input
                              value={endereco.complemento}
                              onChange={(e) => updateEndereco(index, 'complemento', e.target.value)}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Bairro</Label>
                            <Input
                              value={endereco.bairro}
                              onChange={(e) => updateEndereco(index, 'bairro', e.target.value)}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Cidade</Label>
                            <Input
                              value={endereco.cidade}
                              onChange={(e) => updateEndereco(index, 'cidade', e.target.value)}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Estado</Label>
                            <Input
                              value={endereco.estado}
                              onChange={(e) => updateEndereco(index, 'estado', e.target.value.toUpperCase().substring(0, 2))}
                              maxLength={2}
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">CEP</Label>
                            <Input
                              value={endereco.cep}
                              onChange={(e) => updateEndereco(index, 'cep', formatCEP(e.target.value))}
                              className="h-8"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ABA CONTATOS */}
            <TabsContent value="contatos" className="space-y-6 mt-4">
              {/* Contato Principal */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Contato Principal
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Telefone</Label>
                    <Input
                      value={form.telefone}
                      onChange={(e) => setForm(prev => ({ ...prev, telefone: formatPhone(e.target.value) }))}
                      placeholder="(00) 0000-0000"
                    />
                  </div>
                  <div>
                    <Label>Celular</Label>
                    <Input
                      value={form.celular}
                      onChange={(e) => setForm(prev => ({ ...prev, celular: formatPhone(e.target.value) }))}
                      placeholder="(00) 00000-0000"
                    />
                  </div>
                  <div>
                    <Label>E-mail</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="contato@empresa.com"
                    />
                  </div>
                  <div>
                    <Label>Site</Label>
                    <Input
                      value={form.site}
                      onChange={(e) => setForm(prev => ({ ...prev, site: e.target.value }))}
                      placeholder="www.empresa.com.br"
                    />
                  </div>
                </div>
              </div>

              {/* Contatos Adicionais */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Contatos Adicionais
                  </h3>
                  <Button type="button" variant="outline" size="sm" onClick={addContato}>
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar Contato
                  </Button>
                </div>

                {form.contatos.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4 border-2 border-dashed rounded-lg">
                    Nenhum contato adicional cadastrado
                  </p>
                ) : (
                  <div className="space-y-4">
                    {form.contatos.map((contato, index) => (
                      <div key={contato.id} className="p-4 border rounded-lg bg-gray-50 relative">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute top-2 right-2 h-6 w-6 text-red-500 hover:text-red-700"
                          onClick={() => removeContato(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Nome *</Label>
                            <Input
                              value={contato.nome}
                              onChange={(e) => updateContato(index, 'nome', e.target.value)}
                              placeholder="Nome do contato"
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Cargo</Label>
                            <Input
                              value={contato.cargo}
                              onChange={(e) => updateContato(index, 'cargo', e.target.value)}
                              placeholder="Ex: Gerente Compras"
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Departamento</Label>
                            <Input
                              value={contato.departamento}
                              onChange={(e) => updateContato(index, 'departamento', e.target.value)}
                              placeholder="Ex: Comercial"
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Telefone</Label>
                            <Input
                              value={contato.telefone}
                              onChange={(e) => updateContato(index, 'telefone', formatPhone(e.target.value))}
                              placeholder="(00) 0000-0000"
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Celular</Label>
                            <Input
                              value={contato.celular}
                              onChange={(e) => updateContato(index, 'celular', formatPhone(e.target.value))}
                              placeholder="(00) 00000-0000"
                              className="h-8"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">E-mail</Label>
                            <Input
                              type="email"
                              value={contato.email}
                              onChange={(e) => updateContato(index, 'email', e.target.value)}
                              placeholder="email@empresa.com"
                              className="h-8"
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="flex items-center gap-2 cursor-pointer mt-2">
                              <input
                                type="checkbox"
                                checked={contato.principal}
                                onChange={(e) => updateContato(index, 'principal', e.target.checked)}
                                className="w-4 h-4"
                              />
                              <span className="text-sm">Contato principal</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ABA DADOS BANCÁRIOS */}
            <TabsContent value="bancario" className="space-y-4 mt-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <Landmark className="w-4 h-4" />
                Dados Bancários
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Banco</Label>
                  <Input
                    value={form.dados_bancarios.banco}
                    onChange={(e) => setForm(prev => ({ 
                      ...prev, 
                      dados_bancarios: { ...prev.dados_bancarios, banco: e.target.value }
                    }))}
                    placeholder="Nome do banco"
                  />
                </div>
                <div>
                  <Label>Agência</Label>
                  <Input
                    value={form.dados_bancarios.agencia}
                    onChange={(e) => setForm(prev => ({ 
                      ...prev, 
                      dados_bancarios: { ...prev.dados_bancarios, agencia: e.target.value }
                    }))}
                    placeholder="0000"
                  />
                </div>
                <div>
                  <Label>Conta</Label>
                  <Input
                    value={form.dados_bancarios.conta}
                    onChange={(e) => setForm(prev => ({ 
                      ...prev, 
                      dados_bancarios: { ...prev.dados_bancarios, conta: e.target.value }
                    }))}
                    placeholder="00000-0"
                  />
                </div>
                <div>
                  <Label>Tipo de Conta</Label>
                  <Select
                    value={form.dados_bancarios.tipo}
                    onValueChange={(value) => setForm(prev => ({ 
                      ...prev, 
                      dados_bancarios: { ...prev.dados_bancarios, tipo: value }
                    }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="corrente">Conta Corrente</SelectItem>
                      <SelectItem value="poupanca">Conta Poupança</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Chave PIX</Label>
                  <Input
                    value={form.dados_bancarios.pix}
                    onChange={(e) => setForm(prev => ({ 
                      ...prev, 
                      dados_bancarios: { ...prev.dados_bancarios, pix: e.target.value }
                    }))}
                    placeholder="CNPJ, E-mail, Telefone ou Chave Aleatória"
                  />
                </div>
              </div>
            </TabsContent>

            {/* ABA ANEXOS - Documentação */}
            <TabsContent value="anexos" className="space-y-4 mt-4">
              <AnexosErrorBoundary>
                <AnexosManager
                  anexos={form.anexos || []}
                  onChange={(novos) => setForm(prev => ({ ...prev, anexos: novos }))}
                  maxFiles={30}
                  maxSizeMB={50}
                  label="Documentação"
                  modulo="empresas"
                  entidadeId={editingEmpresa?.id || 'nova'}
                  categoria="documentacao"
                />
              </AnexosErrorBoundary>
            </TabsContent>

            {/* ABA ADICIONAL */}
            <TabsContent value="adicional" className="space-y-4 mt-4">
              {/* Observações */}
              <div>
                <Label>Observações</Label>
                <Textarea
                  value={form.observacoes}
                  onChange={(e) => setForm(prev => ({ ...prev, observacoes: e.target.value }))}
                  placeholder="Observações gerais sobre a empresa..."
                  rows={4}
                />
              </div>

              {/* Integrações - Conta Azul (só aparece ao editar) */}
              {editingEmpresa && (
                <div className="space-y-4 mt-6">
                  <h3 className="font-medium text-gray-900 flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Integrações
                  </h3>
                  
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                          <span className="text-white font-bold text-sm">CA</span>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">Conta Azul</h4>
                          <p className="text-sm text-gray-500">
                            {editingEmpresa.conta_azul?.integrado 
                              ? 'Integração ativa - Contas a pagar sincronizadas automaticamente'
                              : 'Conecte para sincronizar pedidos com o financeiro'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {editingEmpresa.conta_azul?.integrado ? (
                          <>
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Conectado
                            </Badge>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleConectarContaAzul(editingEmpresa.id)}
                              data-testid="btn-reconectar-conta-azul"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Reconectar
                            </Button>
                          </>
                        ) : (
                          <Button 
                            onClick={() => handleConectarContaAzul(editingEmpresa.id)}
                            className="bg-blue-600 hover:bg-blue-700"
                            data-testid="btn-conectar-conta-azul"
                          >
                            <Link2 className="w-4 h-4 mr-2" />
                            Conectar ao Conta Azul
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {editingEmpresa.conta_azul?.integrado && editingEmpresa.conta_azul?.connected_at && (
                      <p className="text-xs text-gray-400 mt-2">
                        Conectado em: {new Date(editingEmpresa.conta_azul.connected_at).toLocaleString('pt-BR')}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={submitLoading} data-testid="btn-save-empresa">
              {submitLoading ? 'Salvando...' : editingEmpresa ? 'Atualizar' : 'Criar Empresa'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Dialog Desativar */}
      <AlertDialog open={!!deleteEmpresaId} onOpenChange={() => setDeleteEmpresaId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar empresa?</AlertDialogTitle>
            <AlertDialogDescription>
              A empresa será desativada e não aparecerá mais nas opções de cotações e pedidos.
              Esta ação pode ser revertida posteriormente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Empresas;
